import { useCallback, useRef, useState } from 'react';

/**
 * Detecta se o conteúdo de um elemento excede sua caixa visível
 * (`scrollWidth > clientWidth`), reagindo a mudanças de largura via
 * `ResizeObserver` — cobre resize de janela e colunas/containers com largura
 * flexível, não só o tamanho no mount.
 *
 * Usa um callback ref (em vez de `useRef` + `useLayoutEffect`) para lidar
 * corretamente com elementos que remontam (ex.: um `key` que força
 * remontagem) — o observer é desconectado do nó antigo e reconectado ao novo
 * automaticamente, sem depender de um efeito com deps vazias.
 *
 * @returns `ref` para anexar ao elemento medido e `isOverflowing`.
 *
 * @example
 * ```tsx
 * const { ref, isOverflowing } = useIsOverflowing<HTMLSpanElement>();
 * const content = <span ref={ref} className="truncate">{value}</span>;
 * return isOverflowing ? <Tooltip title={value}>{content}</Tooltip> : content;
 * ```
 */
export function useIsOverflowing<T extends HTMLElement>() {
  const [isOverflowing, setIsOverflowing] = useState(false);
  const observerRef = useRef<ResizeObserver | null>(null);

  const ref = useCallback((el: T | null) => {
    observerRef.current?.disconnect();
    if (!el) return;

    const check = () => setIsOverflowing(el.scrollWidth > el.clientWidth);
    check();

    const observer = new ResizeObserver(check);
    observer.observe(el);
    observerRef.current = observer;
  }, []);

  return { ref, isOverflowing } as const;
}
